import React, { Component } from 'react';
import PropTypes from 'prop-types';
import cx from 'classnames/bind';
import Button from '../Button/Button';
import Menubar from '../Menubar/Menubar';

/**
 * `Tabs` is a group container that only shows the content for the current selected tab.
 *
 * You can add components of any type to this container as long as the immediate children has the prop *name* set as this
 * value will be used as tab name. You may also pass an array of children. Children inside React Fragments will be
 * interpreted as one tab.
 * ````
 * <Tabs>
 *   <div name="Tab 1">...</div>
 *   <div name="Tab 2">...</div>
 *   {arrayOfTabs}
 *   ...
 * </Tabs>
 * ````
 * Only the selected tab panel will be mounted. Selecting another tab will unmount the current tab panel and mount the new
 * one.
 *
 * @property {node} children The tabs and their content you want to add to this container. Each child must have a *name* prop
 * which defines the title to show as selectable tabs. `null` or `undefined` children will be filtered out and will not be
 * reflected in the selection index. The child can also have a *disabled* prop that will disable the tab button.
 * @property {string} className The class name of this container. Will be appended to "core-tabs".
 * @property {boolean} collapsable If set to `true`, the menubar items will be collapsed to a dropdown when there is not
 * enough room to display all items. This requires that the CSS styles the menubar in such a way that the items will not
 * overflow. Defaults to `true`.
 * @property {function} onChange A callback which is triggered when a tab is selected. The callback is called with the
 * index of the tab and the source event. If not defined, this component will keep the selected tab state internally.
 * @property {number} selected The selected tab index. Defaults to 0.
 * @property {boolean} showUnderline If `true`, selecting a tab will show an animated line under the tabs. Defaults to
 * `true`.
 *
 * @see Menubar
 */
export default class Tabs extends Component {

  static displayName = 'Tabs';

  static propTypes = {
    children: PropTypes.node,
    className: PropTypes.string,
    collapsable: PropTypes.bool, // eslint-disable-line react/no-unused-prop-types
    onChange: PropTypes.func,
    selected: PropTypes.number,
    showUnderline: PropTypes.bool, // eslint-disable-line react/no-unused-prop-types
  };

  static defaultProps = {
    collapsable: true,
    selected: 0,
    showUnderline: true,
  };

  state = {
    selected: 0,
  };

  onChange = (e, index) => {
    if (this.props.onChange) {
      this.props.onChange(e, index);
    } else {
      this.setState({ selected: index });
    }
  };

  render() {
    const { children = [], className, onChange, ...rest } = this.props; // eslint-disable-line no-unused-vars
    const panels = React.Children.toArray(children);
    const selected = this.state.selected || Math.max(0, Math.min(panels.length - 1, this.props.selected)) || 0;
    const tabs = panels
      .map((item, index) =>
        <Button key={index} className={cx({ active: index === selected })} disabled={item.props.disabled}>
          {item.props.name || `Untitled ${index}`}
        </Button>
      );

    return (
      <div className={cx('core-tabs', className)}>
        <Menubar {...rest} className={cx('core-tabs-menubar', className ? `${className}-menubar` : undefined)}
          selected={selected} onClick={this.onChange}>
          {tabs}
        </Menubar>
        <div className={cx('tab-content', className ? `${className}-content` : undefined)}>
          {panels[selected]}
        </div>
      </div>
    );
  }

}
